/**
 * Not Found (404) Page
 * Feature: 034-global-error-handling
 * Constitution: v1.11.0 - Principle XI (Web Application Architecture)
 * 
 * @description 404 error page with helpful navigation options
 * @implements FR-017, FR-018
 * @author Peter Dobler
 * @created 2026-01-20
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

/**
 * Props for NotFound page
 */
export interface NotFoundProps {
  /** Custom message to display */
  message?: string;
}

/**
 * Not Found Page Component
 * FR-017: Styled 404 page
 */
export const NotFound: React.FC<NotFoundProps> = ({
  message = "The page you're looking for doesn't exist or has been moved.",
}) => {
  const navigate = useNavigate();

  /**
   * Handle go back navigation
   */
  const handleGoBack = (): void => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div
      className="not-found-page"
      role="main"
      aria-labelledby="not-found-title"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        backgroundColor: '#f9fafb',
        textAlign: 'center',
      }}
    >
      {/* 404 Icon */}
      <div
        style={{
          width: '8rem',
          height: '8rem',
          marginBottom: '2rem',
          backgroundColor: '#e0e7ff',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        aria-hidden="true"
      >
        <svg
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#4f46e5"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M9 10h.01M15 10h.01" />
          <path d="M9.5 15.5a3.5 3.5 0 0 1 5 0" />
        </svg>
      </div>

      {/* Error Code */}
      <h1
        id="not-found-title"
        style={{
          fontSize: '6rem',
          fontWeight: '700',
          color: '#4f46e5',
          margin: '0',
          lineHeight: '1',
        }}
      >
        404
      </h1>

      {/* Error Title */}
      <h2
        style={{
          fontSize: '1.5rem',
          fontWeight: '600',
          color: '#111827',
          margin: '1rem 0 0.5rem',
        }}
      >
        Page Not Found
      </h2>

      {/* Error Description */}
      <p
        style={{
          color: '#6b7280',
          maxWidth: '24rem',
          marginBottom: '2rem',
        }}
      >
        {message}
      </p>

      {/* Action Buttons */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          width: '100%',
          maxWidth: '16rem',
        }}
      >
        {/* Go to Dashboard - FR-075 */}
        <Link
          to="/"
          style={{
            display: 'inline-block',
            padding: '0.75rem 1.5rem',
            backgroundColor: '#4f46e5',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            fontWeight: '500',
            textAlign: 'center',
            transition: 'background-color 0.15s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#4338ca')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#4f46e5')}
        >
          Go to Dashboard
        </Link>

        {/* Go Back Button */}
        <button
          onClick={handleGoBack}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: 'transparent',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'background-color 0.15s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          Go Back
        </button>
      </div>

      {/* Help Links - FR-076 */}
      <div
        style={{
          marginTop: '3rem',
          borderTop: '1px solid #e5e7eb',
          paddingTop: '1.5rem',
        }}
      >
        <p
          style={{
            color: '#6b7280',
            fontSize: '0.875rem',
            marginBottom: '0.5rem',
          }}
        >
          Need help?
        </p>
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
          }}
        >
          <Link
            to="/help"
            style={{
              color: '#4f46e5',
              textDecoration: 'none',
              fontSize: '0.875rem',
            }}
          >
            Help Center
          </Link>
          <span style={{ color: '#d1d5db' }}>|</span>
          <Link
            to="/support"
            style={{
              color: '#4f46e5',
              textDecoration: 'none',
              fontSize: '0.875rem',
            }}
          >
            Contact Support
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
